import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/getToken';
import CloseIcon from '@mui/icons-material/Close';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://devapi.quickcare.ng/admin';

const EditProfileModal = ({ isOpen, onClose, onSave }) => {
    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Fetch current profile data when modal opens
        if (isOpen) {
            const fetchProfile = async () => {
                const token = getToken();
                const myHeaders = new Headers();
                myHeaders.append("Accept", "application/json");
                myHeaders.append("Authorization", `Bearer ${token}`);

                try {
                    const response = await fetch(`${baseURL}/profile`, {
                        method: "GET",
                        headers: myHeaders,
                    });
                    const result = await response.json();
                    if (response.ok) {
                        setFullName(result.data.full_name || '');
                        setPhoneNumber(result.data.phone_number || '');
                    }
                } catch (error) {
                    console.error('Error fetching profile:', error);
                }
            };
            fetchProfile();
        }
    }, [isOpen]);

    const handleSave = async () => {
        if (!fullName.trim()) {
            setError('Full name is required');
            return;
        }

        setIsLoading(true);
        setError('');

        const token = getToken();
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", `Bearer ${token}`);

        const raw = JSON.stringify({
            full_name: fullName,
            phone_number: phoneNumber
        });

        const requestOptions = {
            method: "PUT",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        try {
            const response = await fetch(`${baseURL}/profile`, requestOptions);
            const result = await response.json();

            if (response.ok) {
                onSave(result.data);
                onClose();
            } else {
                setError(result.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error("Fetch error:", error);
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose}></div>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative z-10 animate-slideUp">
                <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-100">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Edit Profile</h2>
                        <p className="text-xs text-gray-400">Update your personal information</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <CloseIcon className="text-gray-500" />
                    </button>
                </div>

                <div className="p-4 lg:p-6 space-y-4">
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                            <span>⚠️</span>
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name
                        </label>
                        <input
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 text-gray-800"
                            placeholder="Enter your full name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                        </label>
                        <input
                            type="text"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 text-gray-800"
                            placeholder="Enter your phone number"
                        />
                    </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 p-4 lg:p-6 border-t border-gray-100">
                    <button 
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={isLoading}
                        className={`px-6 py-2.5 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                            isLoading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-[#069125] hover:bg-[#057a1f] hover:shadow-lg hover:shadow-[#069125]/30'
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                            </>
                        ) : (
                            '💾 Save Changes'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditProfileModal;